import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useLogin } from './hooks/useLogin';
import { ChooseLoginModal } from '@/bo-kit/components';
import { useNavigate } from 'react-router-dom';
import { UserType } from '@/bo-kit/types';
import { useReturnUrl } from '@/bo-kit/hooks/useReturnUrl';
import { SignInPageFormInner } from '@/bo-kit/components/SignInPage/components/Form/components/FormInner';
import { useDispatch } from 'react-redux';
// import { useNavigateAfterLogin } from '@/bo-kit/components/SignInPage/components/Form/hooks/useNavigateAfterLogin';
import { LoginRequest, LoginAndResetPassErrorCode } from '@/bo-kit/transport/types';

export const Form: FC = () => {
    const {
        isLoading,
        isError,
        // isSuccess,
        doLogin,
        roles,
        errorMessage,
        error,
        verifyCodeParams,
        reset,
    } = useLogin();

    const [returnUrl] = useReturnUrl();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (error === LoginAndResetPassErrorCode.NOT_ACTIVATED_USER && verifyCodeParams) {
            navigate('verify', {
                state: verifyCodeParams,
            });
        }
    }, [dispatch, error, navigate, verifyCodeParams]);

    const { t } = useTranslation('bo-kit__signin');
    const { t: validationT } = useTranslation('bo-kit__validation');

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                email: Yup.string().required(validationT('Field is required')),
                password: Yup.string().required(validationT('Field is required')),
            }),
        [validationT]
    );

    //   const navigateAfterLogin = useNavigateAfterLogin(isSuccess);

    const onSubmit = useCallback(
        (arg: LoginRequest, userType?: UserType) => {
            if (userType !== undefined) {
                doLogin({ userType: userType, password: arg.password, email: arg.email });
            } else {
                doLogin(arg);
            }
        },
        [doLogin]
    );

    const goToSignup = useCallback(
        () => navigate('signup', { state: { returnUrl } }),
        [navigate, returnUrl]
    );

    const getOnSelectRoleToLogin = useCallback(
        (formValues: LoginRequest) => (userType: UserType) => {
            onSubmit(formValues, userType);
        },
        [onSubmit]
    );

    //   if (navigateAfterLogin) {
    //     return navigateAfterLogin;
    //   }

    return (
        <Formik<LoginRequest>
            initialValues={{ email: '', password: '' }}
            onSubmit={(data) => onSubmit(data)}
            validationSchema={validationSchema}
        >
            {(formik) => (
                <>
                    <SignInPageFormInner
                        formik={formik}
                        isError={isError}
                        error={error}
                        errorMessage={errorMessage}
                        disabled={isLoading}
                        goToSignup={goToSignup}
                    />
                    {roles && (
                        <ChooseLoginModal
                            roles={roles}
                            btnCaption={t('Log in as {{ROLE}}')}
                            onCancel={reset}
                            onSelect={getOnSelectRoleToLogin(formik.values)}
                        />
                    )}
                </>
            )}
        </Formik>
    );
};
